import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../../axios/request';
import config from '../../config/configuration_keys.json';
import AdminLoader from '../../components/adminLoader';

const InvestmentBanner = () => {
  const navigate = useNavigate();
  const REACT_APP_API_URL = config.REACT_APP_API_URL;
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('user');
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [navigate]);

  const [banner, setBanner] = useState({
    id: null,
    title: '',
    description: '',
    image: null,
    newImage: null,
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const fetchBanner = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/admin/investment/getInvestmentSections');
      if (response?.data) {
        setBanner({
          id: response?.data[0].id || null,
          title: response?.data[0].title || '',
          description: response?.data[0].description || '',
          image: response?.data[0].image || null,
          newImage: null,
        });
        setIsLoading(false);
      }
    } catch (error) {
      setErrorMessage('Failed to load banner. Please try again.');
    }
  };

  useEffect(() => {
    fetchBanner();
  }, []);

  const handleInputChange = (field, value) => {
    setBanner((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setIsLoading(true);

    const formData = new FormData();
    formData.append('section_name', "banner");
    formData.append('title', banner.title);
    formData.append('description', banner.description);
    if (banner.newImage) {
      formData.append('image', banner.newImage);
    } else {
      formData.append('image', banner.image);
    }

    if (banner.id) {
      formData.append('id', banner.id);
    }

    try {
      const response = await apiClient.post('/admin/investment/addOrUpdateInvestmentSection1', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      fetchBanner();
      setIsLoading(false);
      window.scrollTo({
        top: 0,
        behavior: 'smooth',  // This makes the scroll smooth
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error saving banner:', error);
      setErrorMessage('Failed to save banner. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div style={styles.mainContainer}>
      <div style={styles.headerWrapper}>
        <div style={styles.header}>
          <div style={styles.breadcrumbs}>
            <Link to="/dashboard" style={styles.breadcrumbLink}>Dashboard</Link>
            <span style={styles.breadcrumbSeparator}>/</span>
            <Link to="/admin/investment" style={styles.breadcrumbLink}>Investment Page</Link>
            <span style={styles.breadcrumbSeparator}>/</span>
            <span style={styles.currentPage}>Section 1</span>
          </div>
          <div style={styles.headerRight}>
            <button style={styles.logoutButton} onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={styles.contentContainer}>
        {isLoading ? (
          <AdminLoader />
        ) : (
          <>
            <h1 style={styles.title}>Section 1 Page</h1>
            <div style={styles.card}>
              <form id="investmentBannerForm" onSubmit={handleSubmit}>
                <div style={styles.bannerItem}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Banner Title</label>
                    <input
                      type="text"
                      placeholder="Enter banner title"
                      value={banner.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Banner Description</label>
                    <input
                      type="text"
                      placeholder="Enter banner text"
                      value={banner.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Banner Image</label>
                    {banner.image && (
                      <div style={styles.imagePreview}>
                        <img
                          src={`${REACT_APP_API_URL}/uploads/${banner.image}`}
                          alt="Investment Banner"
                          style={styles.image}
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleInputChange('newImage', e.target.files[0])}
                      style={styles.input}
                    />
                  </div>
                </div>

                {successMessage && (
                  <div style={styles.successMessage}>{successMessage}</div>
                )}

                {errorMessage && (
                  <div style={styles.errorMessage}>{errorMessage}</div>
                )}

                <div style={styles.bottomActions}>
                  <button onClick={handleBack} style={styles.backButton}>
                    ← Back
                  </button>
                  <button type="submit" style={styles.submitButton}>
                    Save
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  mainContainer: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  headerWrapper: {
    width: '100%',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '15px 20px',
  },
  breadcrumbs: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  title: {
    textAlign: 'center',
    marginBottom: '30px',
    color: '#343a40',
  },
  breadcrumbLink: {
    color: '#666',
    textDecoration: 'none',
    fontSize: '14px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  breadcrumbSeparator: {
    color: '#999',
    fontSize: '14px',
  },
  currentPage: {
    color: '#333',
    fontWeight: '500',
    fontSize: '14px',
  },
  headerRight: {
    display: 'flex',
    gap: '10px',
  },
  contentContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 20px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  bannerItem: {
    marginBottom: '30px',
    padding: '20px',
    borderRadius: '4px',
    backgroundColor: 'rgb(235 233 233)',
  },
  inputGroup: {
    marginBottom: '15px',
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    color: '#666',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginBottom: '10px',
  },
  imagePreview: {
    marginBottom: '15px',
  },
  image: {
    maxWidth: '100px',
    height: 'auto',
    borderRadius: '4px',
  },
  bottomActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0',
  },
  rightButtons: {
    display: 'flex',
    gap: '10px',
  },
  backButton: {
    padding: '10px 20px',
    backgroundColor: '#f0f0f0',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  addButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  submitButton: {
    backgroundColor: '#2196F3',
    color: 'white',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#1976D2',
    },
  },
  removeButton: {
    backgroundColor: '#f44336',
    color: 'white',
    padding: '8px 15px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '10px',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  logoutButton: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#0056b3',
    },
  },
  successMessage: {
    marginTop: '20px',
    padding: '10px',
    backgroundColor: '#d4edda',
    color: '#155724',
    border: '1px solid #c3e6cb',
    borderRadius: '4px',
    textAlign: 'center',
  },

  errorMessage: {
    marginTop: '20px',
    padding: '10px',
    backgroundColor: '#f2dede',
    color: '#a94442',
    borderRadius: '4px',
    textAlign: 'center',
  },
};

export default InvestmentBanner;
